import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./Pages.css";

const Solutions = () => {
  const solutions = [
    {
      category: "Data Intelligence & Insights",
      description:
        "High-quality, targeted data solutions designed to help your business identify the right audience, improve decision-making, and maximize ROI.",
      benefits: [
        "Accurate, verified data",
        "Customizable industry and role-based targeting",
        "Insights for smarter decision-making",
      ],
    },
    {
      category: "Digital Marketing & Lead Generation",
      description:
        "We design and manage marketing campaigns that generate qualified leads and increase brand visibility across digital channels.",
      benefits: [
        "Email marketing & automation",
        "Social media campaigns",
        "Search engine optimization (SEO)",
        "Lead nurturing strategies",
      ],
    },
    {
      category: "Business Growth Consulting",
      description:
        "Strategic consulting to identify growth opportunities, optimize performance, and scale operations efficiently.",
      benefits: [
        "Market research & analysis",
        "Business strategy development",
        "Performance tracking & optimization",
        "ROI-focused recommendations",
      ],
    },
    {
      category: "Automation & Technology Solutions",
      description:
        "Smart automation and tech tools that streamline operations, reduce manual work, and improve business efficiency.",
      benefits: [
        "AI-powered workflow automation",
        "Software solutions tailored to your business",
        "Integration with existing tools and systems",
        "Continuous monitoring & optimization",
      ],
    },
    {
      category: "Custom Solutions",
      description:
        "Every business is unique — we create custom solutions designed specifically to solve your challenges and meet your goals.",
      benefits: [
        "Tailored to your industry and business size",
        "Combination of data, marketing, and tech solutions",
        "Scalable strategies for long-term growth",
        "Dedicated support and guidance",
      ],
    },
  ];

  const steps = [
    {
      number: "1",
      title: "Understand",
      description:
        "We dive deep into your business goals, challenges, and industry to build a strategy that works.",
    },
    {
      number: "2",
      title: "Design",
      description:
        "Crafting solutions that combine data, technology, and expertise to solve your unique challenges.",
    },
    {
      number: "3",
      title: "Execute",
      description:
        "Implementing solutions efficiently, monitoring results, and optimizing continuously to ensure measurable growth.",
    },
    {
      number: "4",
      title: "Scale",
      description:
        "Support for long-term business growth through repeatable, scalable strategies.",
    },
  ];

  return (
    <main>
      <Helmet>
        <title>B2B Solutions | Seagull Partners</title>
        <meta
          name="description"
          content="Explore Seagull Partners’ B2B solutions, from data intelligence and lead generation to digital marketing, automation, and growth consulting that convert."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-hero-title">
            Solutions That Drive Real Business Growth
          </h1>
          <p className="page-hero-subtitle">
            Seagull delivers smart, data-driven, and tailored solutions to
            help your business grow faster, smarter, and more efficiently. From
            strategic insights to technology-driven tools, we create solutions
            that deliver measurable results.
          </p>
          <Link to="/contact" className="btn">
            📩 Get a Free Consultation
          </Link>
        </div>
      </section>

      {/* Solution Categories */}
      <section className="section">
        <div className="container">
          <div className="content-section">
            <h2 className="section-title">Our Solution Categories</h2>
            <p className="section-intro">
              We organize our solutions into{" "}
              <strong>five core categories</strong> to make it simple for
              businesses to understand what we offer:
            </p>

            <div className="solutions-list">
              {solutions.map((solution, index) => (
                <div key={index} className="solution-category">
                  <div className="solution-header">
                    <span className="solution-icon">🔹</span>
                    <h3>
                      {index + 1}. {solution.category}
                    </h3>
                  </div>

                  <div className="solution-content">
                    <div className="solution-description">
                      <h4>What It Is:</h4>
                      <p>{solution.description}</p>
                    </div>

                    <div className="solution-benefits">
                      <h4>Key Benefits:</h4>
                      <ul>
                        {solution.benefits.map((benefit, i) => (
                          <li key={i}>• {benefit}</li>
                        ))}
                      </ul>
                    </div>

                    <Link to="/contact" className="btn solution-cta">
                      ➡ {index === 4 ? "Get a Custom Solution" : "Learn More"}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section process-section">
        <div className="container">
          <div className="content-section">
            <h2 className="section-title">
              From Strategy to Growth — Our Process
            </h2>

            <div className="process-steps">
              {steps.map((step, index) => (
                <div key={index} className="process-step">
                  <div className="process-step-number">{step.number}</div>
                  <div className="process-step-content">
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Our Solutions Work */}
      <section className="section why-solutions-section">
        <div className="container">
          <div className="content-section">
            <h2 className="section-title">Why Our Solutions Work</h2>

            <div className="why-list">
              <div className="why-item">
                • Strategy-first, not one-size-fits-all
              </div>
              <div className="why-item">• Data-driven and results-focused</div>
              <div className="why-item">
                • Flexible and scalable to fit your business
              </div>
              <div className="why-item">• Expert guidance at every step</div>
              <div className="why-item">
                • Proven methodologies that deliver measurable outcomes
              </div>
            </div>

            <div className="cta-container">
              <Link to="/contact" className="btn">
                📩 Talk to Our Experts
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Focus
      <section className="section industries-section">
        <div className="container">
          <div className="content-section">
            <h2 className="section-title">
              Solutions Tailored to Your Industry
            </h2>

            <div className="industries-grid">
              <div className="industry-card">Startups & SMBs</div>
              <div className="industry-card">Enterprise Businesses</div>
              <div className="industry-card">Marketing & Sales Teams</div>
              <div className="industry-card">Technology Companies</div>
              <div className="industry-card">
                Healthcare & Professional Services
              </div>
            </div>

            <p className="industries-conclusion">
              We adapt our solutions to the needs of your industry, ensuring
              maximum impact.
            </p>
          </div>
        </div>
      </section> */}
    </main>
  );
};

export default Solutions;
